/**
 * Gemeinsamer Zustand: die API ist vom Browser aus nicht erreichbar.
 * Netzwerkfehler und Gateway-Antworten (502/503/504) setzen ihn, jede
 * andere HTTP-Antwort hebt ihn wieder auf.
 */

const SHOW_DELAY_MS = 700;
const RECOVERY_RELOAD_KEY = "eh-api-recovery-reload";
const RECOVERY_COOLDOWN_MS = 15_000;

type Listener = (unreachable: boolean) => void;

let unreachable = false;
let showTimer: number | null = null;
let generation = 0;
const listeners = new Set<Listener>();

function emit(value: boolean): void {
  for (const listener of listeners) listener(value);
}

export function isServerUnreachable(): boolean {
  return unreachable;
}

export function subscribeServerReachability(listener: Listener): () => void {
  listeners.add(listener);
  listener(unreachable);
  return () => {
    listeners.delete(listener);
  };
}

export function isGatewayUnavailableStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

function isAbortError(err: unknown): boolean {
  return (
    (typeof DOMException !== "undefined" && err instanceof DOMException && err.name === "AbortError") ||
    (err instanceof Error && err.name === "AbortError")
  );
}

/** Keine HTTP-Antwort (Netzwerk, DNS, Verbindung abgelehnt). Abbrüche zählen nicht. */
export function reportServerUnreachable(): void {
  if (unreachable || showTimer != null) return;
  const token = generation;
  if (typeof window === "undefined") {
    unreachable = true;
    emit(true);
    return;
  }
  showTimer = window.setTimeout(() => {
    showTimer = null;
    if (token !== generation) return;
    unreachable = true;
    emit(true);
  }, SHOW_DELAY_MS);
}

export function reportServerReachable(): void {
  generation += 1;
  if (showTimer != null && typeof window !== "undefined") {
    window.clearTimeout(showTimer);
    showTimer = null;
  }
  if (!unreachable) return;
  unreachable = false;
  emit(false);
}

/** `null` = der Browser hat keine Antwort bekommen. */
export function observeApiExchange(status: number | null): void {
  if (status == null || isGatewayUnavailableStatus(status)) {
    reportServerUnreachable();
    return;
  }
  reportServerReachable();
}

export function observeApiTransportError(err: unknown): void {
  if (isAbortError(err)) return;
  reportServerUnreachable();
}

export function markApiRecoveryReload(): void {
  try {
    sessionStorage.setItem(RECOVERY_RELOAD_KEY, String(Date.now()));
  } catch {
    // Privater Modus
  }
}

export function apiRecoveryReloadOnCooldown(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RECOVERY_RELOAD_KEY) || "0");
    return Number.isFinite(last) && Date.now() - last < RECOVERY_COOLDOWN_MS;
  } catch {
    return false;
  }
}

export async function probeApiHealth(apiBase: string): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 8000);
  try {
    const base = apiBase.replace(/\/$/, "");
    const res = await fetch(`${base}/health`, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
      signal: controller.signal,
    });
    // Jede HTTP-Antwort außer Bad Gateway / Unavailable / Timeout heißt: der Server ist da.
    return !isGatewayUnavailableStatus(res.status);
  } catch {
    return false;
  } finally {
    window.clearTimeout(timer);
  }
}
